import React from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";

function TransactionDetail() {
    const location = useLocation();
    const navigate = useNavigate();
    const txn = location.state?.txn;

   // console.log(txn);

    if (!txn) {
        return <Navigate to="/history" replace />;
    }

    // Map API response fields to component fields
    const {
        id,
        order_id,
        amount,
        consumerName,
        order_type,
        time,
        date,
        cn,
        user_profit,
        txn: transactionRef,
        status,
        biller_name,
        logo
    } = txn;


    // Get transaction type display for UI
    const getTransactionDisplay = (type) => {
        switch (type) {
            case "1":
                return "Payment";
            case "2":
                return "Mobile Recharge";
            case "3":
                return "Bill Payment";
            case "4":
                return "Bonus";
            default:
                return "Transaction";
        }
    };

    // Get status display
    const getStatusDisplay = (statusCode) => {
        switch (statusCode) {
            case "1":
                return { status: "success", label: "Success", color: APP_THEME.successColor };
            case "2":
                return { status: "failed", label: "Failed", color: APP_THEME.errorColor };
            case "0":
                return { status: "pending", label: "Pending", color: APP_THEME.warningColor };
            case "3":
                return { status: "pending", label: "Pending", color: APP_THEME.warningColor };
            default:
                return { status: "pending", label: "Unknown", color: APP_THEME.warningColor };
        }
    };

    const statusInfo = getStatusDisplay(status);
    //console.log(statusInfo);
    const isSuccess = statusInfo.status === "success";
    const isFailed = statusInfo.status === "failed";
    const isPending = statusInfo.status === "pending";
    const formattedAmount = parseFloat(amount).toFixed(2);

    const getStatusTitle = () => {
        if (isFailed) return `Transaction Failed`;
        if (isPending) return `Transaction Pending`;
        if (isSuccess) return `Transaction Successful`;
        return "Transaction Status";
    };

    const getIcon = () => {
        if (isSuccess) {
            return (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.successColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            );
        } else if (isFailed) {
            return (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.errorColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            );
        } else {
            return (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.warningColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                </svg>
            );
        }
    };

    const getBgColor = () => {
        if (isSuccess) return `${APP_THEME.successColor}15`;
        if (isFailed) return `${APP_THEME.errorColor}15`;
        return `${APP_THEME.warningColor}15`;
    };

    const getAmountColor = () => {
        if (isSuccess) return APP_THEME.successColor;
        if (isFailed) return APP_THEME.errorColor;
        return APP_THEME.warningColor;
    };

    const formatDateTime = (dateStr, timeStr) => {
        if (dateStr && timeStr) {
            const dateTimeStr = `${dateStr} ${timeStr}`;
            const d = new Date(dateTimeStr);
            if (!isNaN(d.getTime())) {
                return d.toLocaleString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                });
            }
        }
        const d = new Date(dateStr);
        return d.toLocaleString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    };

    const formatTime = (timeStr) => {
        const d = new Date(`2000-01-01 ${timeStr}`);
        return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    };

    return (
        <>
            <Header title={"Transaction Details"} showBack={true} />
            <div className="p-4 text-center">
                {/* Status Icon */}
                <div
                    className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: getBgColor() }}
                >
                    {getIcon()}
                </div>

                {/* Status Title */}
                <h2
                    className="text-lg font-bold mb-1"
                    style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                >
                    {getStatusTitle()}
                </h2>

                {/* Amount */}
                <div
                    className="text-2xl font-extrabold mb-1"
                    style={{
                        color: getAmountColor(),
                        fontFamily: APP_THEME.fontFamily
                    }}
                >
                    ₹{formattedAmount}
                </div>

                {/* Date */}
                <div
                    className="text-xs mb-6"
                    style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                >
                    {formatDateTime(date, time)}
                </div>

                {/* Transaction Details Card */}
                <div
                    className="rounded-2xl p-5 border text-left mb-6"
                    style={{
                        background: APP_THEME.surfaceColor,
                        borderRadius: APP_THEME.borderRadiusCard,
                        borderColor: APP_THEME.borderColor,
                    }}
                >
                    {/* Order ID */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            Order ID
                        </div>
                        <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            {order_id || id || "N/A"}
                        </div>
                    </div>

                    {/* Transaction Reference */}
                    {transactionRef && (
                        <div className="mb-3">
                            <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                                Operator TXN/RRN
                            </div>
                            <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                                {transactionRef}
                            </div>
                        </div>
                    )}

                    {/* Transaction Type */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            Service Type
                        </div>
                        <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            {order_type}
                        </div>
                    </div>

                    {/* Operator / Biller */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            {order_type === "3" ? "Biller" : "Operator"}
                        </div>
                        <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            {biller_name || "N/A"}
                        </div>
                    </div>

                    {/* Mobile Number / Consumer Number - Updated based on transaction type */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            {order_type === "Recharge" ? "Mobile Number" :
                                order_type === "3" ? "Consumer Number" :
                                    order_type === "1" ? "Mobile ID" : "Reference ID"}
                        </div>
                        <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            {cn || "N/A"}
                        </div>
                    </div>

                    {/* Consumer Name - Show for bill payments and recharges */}
                    {(consumerName && (order_type === "2" || order_type === "3")) && (
                        <div className="mb-3">
                            <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                                Consumer Name
                            </div>
                            <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                                {consumerName}
                            </div>
                        </div>
                    )}

                    {/* Amount */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            Amount
                        </div>
                        <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            ₹{formattedAmount}
                        </div>
                    </div>

                    {/* Cashback/Profit - Only show for non-bonus transactions */}
                    {(order_type !== "4" && user_profit && parseFloat(user_profit) > 0) && (
                        <div className="mb-3">
                            <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                                Cashback Earned
                            </div>
                            <div className="text-sm font-semibold" style={{ color: APP_THEME.successColor, fontFamily: APP_THEME.fontFamily }}>
                                ₹{parseFloat(user_profit).toFixed(2)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons - Hide for bonus transactions */}
                {order_type !== "4" && (
                    <div className="flex gap-3">
                        <button
                            onClick={() => window.print()}
                            className="flex-1 h-12 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 active:scale-98"
                            style={{
                                background: APP_THEME.surfaceColor,
                                color: APP_THEME.textPrimary,
                                border: `1px solid ${APP_THEME.borderColor}`,
                                borderRadius: APP_THEME.borderRadiusBtn,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            Print Receipt
                        </button>
                        <button
                            onClick={() => navigate("/complaint", { state: { txnId: order_id || id } })}
                            className="flex-1 h-12 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 active:scale-98"
                            style={{
                                background: APP_THEME.primaryColor,
                                color: APP_THEME.textOnPrimary,
                                borderRadius: APP_THEME.borderRadiusBtn,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            Raise Complaint
                        </button>
                    </div>
                )}

                {/* Bonus specific message */}
                {order_type === "4" && (
                    <div className="mt-4 p-4 rounded-lg" style={{
                        background: APP_THEME.successColor + "10",
                        border: `1px solid ${APP_THEME.successColor}30`
                    }}>
                        <p className="text-sm" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            🎉 This is a bonus transaction. No action required.
                        </p>
                    </div>
                )}
            </div>

            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                `}
            </style>
        </>
    );
}

export default TransactionDetail;